import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { collection, doc, getDocs, query, where } from '../../firebase.config';
import { writeBatch, addDoc, setGlobalCompanyId } from '../../utils/enforcedTracking';
import { db } from '../../firebase.config';
import { 
  readExcelFile, 
  generateAutoMappings, 
  validateRequiredFields, 
  parseNumericValue, 
  parseDateValue, 
  findDuplicates, 
  showToast, 
  FIELD_DEFINITIONS 
} from './ImportUtils';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const ExpensesImport = () => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [fieldMappings, setFieldMappings] = useState({});
  const [mappedData, setMappedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [existingExpenses, setExistingExpenses] = useState([]);
  
  const fileInputRef = useRef(null);
  const expenseFields = FIELD_DEFINITIONS.expenses;

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);

    try {
      showToast.loading('Reading file...', { id: 'file-read' });
      
      const data = await readExcelFile(file);
      
      if (!data || data.length === 0) {
        showToast.error('No data found in file', { id: 'file-read' });
        return;
      }

      setFileData(data);
      const headers = Object.keys(data[0]);
      setHeaders(headers);
      
      // Auto-select all rows by default
      setSelectedRows(new Set(data.map((_, index) => index)));
      
      // Auto-map common fields
      const autoMappings = generateAutoMappings(headers, expenseFields);
      setFieldMappings(autoMappings);

      showToast.success(`File loaded successfully! Found ${data.length} records.`, { id: 'file-read' });
      setCurrentStep(2);
    } catch (error) {
      console.error('File read error:', error);
      showToast.error(`Failed to read file: ${error.message}`, { id: 'file-read' });
    }
  };

  const handleSelectAll = () => {
    setSelectedRows(new Set(fileData.map((_, index) => index)));
  };

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  const handleRowToggle = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  // Fetch existing expenses for duplicate check
  const fetchExistingExpenses = async () => {
    if (!companyId) return [];
    
    try {
      const expensesRef = collection(db, 'companies', companyId, 'expenses');
      const snapshot = await getDocs(expensesRef);
      const expenses = [];
      snapshot.forEach((doc) => {
        expenses.push({ id: doc.id, ...doc.data() });
      });
      return expenses;
    } catch (error) {
      console.error('Error fetching existing expenses:', error);
      return [];
    }
  };

  const generateMappedPreview = async () => {
    const selectedData = fileData.filter((_, index) => selectedRows.has(index));
    
    // Validate required fields
    const errors = validateRequiredFields(selectedData, expenseFields);
    if (errors.length > 0) {
      showToast.error(`Validation errors: ${errors.join(', ')}`);
      return;
    }
    
    // Fetch existing expenses for duplicate check
    const existing = await fetchExistingExpenses();
    setExistingExpenses(existing);
    
    const mapped = selectedData.map((row, index) => {
      const expense = {
        id: `temp_${index}`,
        head: '',
        amount: 0,
        date: '',
        description: '',
        paymentMode: 'Cash',
        group: 'Fixed',
        employeeName: '',
        receiptNumber: '',
        notes: '',
        isDuplicate: false,
        duplicateAction: 'skip',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Map fields based on user selection
      Object.entries(fieldMappings).forEach(([fieldKey, columnName]) => {
        if (columnName && row[columnName] !== undefined) {
          const value = row[columnName];
          
          switch (fieldKey) {
            case 'head':
              expense.head = String(value).trim() || `Expense ${index + 1}`;
              break;
            case 'amount':
              expense.amount = parseNumericValue(value, 0);
              break;
            case 'date':
              expense.date = parseDateValue(value) || new Date().toISOString();
              break;
            case 'description':
              expense.description = String(value).trim();
              break;
            case 'paymentMode':
              const mode = String(value).trim().toLowerCase();
              expense.paymentMode = mode.includes('card') ? 'Card' : 
                                 mode.includes('bank') ? 'Bank Transfer' : 
                                 mode.includes('upi') ? 'UPI' : 'Cash';
              break;
            case 'group':
              expense.group = String(value).trim() || 'Fixed';
              break;
            case 'employeeName':
              expense.employeeName = String(value).trim();
              break;
            case 'receiptNumber':
              expense.receiptNumber = String(value).trim();
              break;
            case 'notes':
              expense.notes = String(value).trim();
              break;
          }
        }
      });

      return expense;
    });

    // Check for duplicates based on head and amount
    const mappedWithDuplicates = mapped.map(expense => {
      const duplicate = existing.find(existingExpense => 
        existingExpense.head && 
        String(existingExpense.head).toLowerCase() === String(expense.head).toLowerCase() &&
        Math.abs(parseNumericValue(existingExpense.amount, 0) - parseNumericValue(expense.amount, 0)) < 0.01
      );
      
      return {
        ...expense,
        isDuplicate: !!duplicate,
        duplicateId: duplicate?.id,
        existingItem: duplicate
      };
    });
    
    setMappedData(mappedWithDuplicates);
    setCurrentStep(3);
  };

  // Handle editing individual expenses
  const handleEditExpense = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index, updatedExpense) => {
    const newMappedData = [...mappedData];
    newMappedData[index] = { ...updatedExpense, id: `temp_${index}` };
    setMappedData(newMappedData);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  // Handle duplicate action changes
  const handleDuplicateActionChange = (index, action) => {
    const newMappedData = [...mappedData];
    newMappedData[index].duplicateAction = action;
    setMappedData(newMappedData);
  };

  const handleImport = async () => {
    if (!companyId) {
      showToast.error('No company selected');
      return;
    }
    
    // 🚦 Enable automatic operation tracking for imports
    setGlobalCompanyId(companyId);

    if (!mappedData || mappedData.length === 0) {
      showToast.error('No data to import');
      return;
    }

    setIsImporting(true);
    try {
      showToast.loading('Importing expenses...', { id: 'import' });
      
      const batch = writeBatch(db);
      const expensesRef = collection(db, 'companies', companyId, 'expenses');
      
      let successCount = 0;
      let errorCount = 0;

      for (const expense of mappedData) {
        try {
          // Validate required fields
          if (!expense.head || expense.head.trim() === '' || expense.amount <= 0) {
            console.warn('Skipping expense with invalid data:', expense);
            errorCount++;
            continue;
          }

          // Handle duplicates
          if (expense.isDuplicate && expense.duplicateAction === 'skip') {
            console.log('Skipping duplicate expense:', expense.head);
            continue;
          }

          const expenseData = {
            head: expense.head.trim(),
            amount: parseNumericValue(expense.amount, 0),
            date: expense.date,
            description: expense.description || '',
            paymentMode: expense.paymentMode || 'Cash',
            group: expense.group || 'Fixed',
            employeeName: expense.employeeName || '',
            receiptNumber: expense.receiptNumber || '',
            notes: expense.notes || '',
            isActive: true,
            companyId: companyId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          if (expense.isDuplicate && expense.duplicateAction === 'update') {
            // Update existing expense
            const docRef = doc(db, 'companies', companyId, 'expenses', expense.duplicateId);
            batch.update(docRef, expenseData);
            successCount++;
          } else if (expense.isDuplicate && expense.duplicateAction === 'merge') {
            // Merge with existing expense
            const existing = expense.existingItem;
            const mergedData = {
              ...expenseData,
              amount: parseNumericValue(existing.amount, 0) + parseNumericValue(expense.amount, 0),
              description: expense.description || existing.description || '',
              notes: expense.notes || existing.notes || ''
            };
            const docRef = doc(db, 'companies', companyId, 'expenses', expense.duplicateId);
            batch.update(docRef, mergedData);
            successCount++;
          } else {
            // Create new expense
            const docRef = doc(expensesRef);
            batch.set(docRef, expenseData);
            successCount++;
          }
        } catch (error) {
          console.error('Error preparing expense:', error, expense);
          errorCount++;
        }
      }

      await batch.commit();
      
      setImportResult({
        success: successCount,
        errors: errorCount,
        total: mappedData.length
      });
      
      showToast.success(`Import completed! ${successCount} expenses imported successfully.`, { id: 'import' });
      setCurrentStep(4);
    } catch (error) {
      console.error('Import error:', error);
      showToast.error(`Import failed: ${error.message}`, { id: 'import' });
      setImportResult({
        success: 0,
        errors: mappedData.length,
        total: mappedData.length
      });
      setCurrentStep(4);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Expenses from Excel</h3>
              <p className="text-gray-600 mb-8">Upload your Excel file to import expenses in bulk</p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <div className="text-6xl text-purple-500 mb-4">💰</div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Select Excel File</h4>
              <p className="text-gray-600 mb-6">Choose an Excel file (.xlsx, .xls) containing your expenses data</p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer text-lg font-medium"
              >
                Choose Excel File
              </label>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Preview & Select Rows</h3>
              <div className="text-sm text-gray-600">
                {selectedRows.size} of {fileData.length} rows selected
              </div>
            </div>

            {/* Row Selection Controls */}
            <div className="flex space-x-4">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Deselect All
              </button>
            </div>

            {/* Data Preview Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-12">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === fileData.length}
                          onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        />
                      </th>
                      {headers.map((header, index) => (
                        <th key={index} className="px-4 py-3 text-left font-medium text-gray-700">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.map((row, index) => (
                      <tr key={index} className={`border-t ${selectedRows.has(index) ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(index)}
                            onChange={() => handleRowToggle(index)}
                          />
                        </td>
                        {headers.map((header, colIndex) => (
                          <td key={colIndex} className="px-4 py-3 text-gray-600">
                            {String(row[header] || '').substring(0, 50)}
                            {String(row[header] || '').length > 50 && '...'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Field Mapping Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Map Excel Columns to Expense Fields</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {expenseFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <AppSelect
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => setFieldMappings({...fieldMappings, [field.key]: e.target.value})}
                      placeholder="Select Column"
                      aria-label={field.label}
                      sheetTitle={field.label}
                      options={headers.map(header => ({ value: header, label: header }))}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:ring-purple-500`}
                    />
                    <p className="text-xs text-gray-500">{field.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={generateMappedPreview}
                disabled={selectedRows.size === 0 || !fieldMappings.head || !fieldMappings.amount}
                className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Preview
              </button>
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Mapped Data Preview</h3>
              <div className="text-sm text-gray-600">
                {mappedData.length} expenses ready to import
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-purple-800">
                <strong>Preview:</strong> Review the mapped data below. All selected rows will be imported as expenses.
              </div>
            </div>

            {/* Mapped Data Preview */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Head</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Amount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Payment Mode</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Group</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Employee</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedData.map((expense, index) => (
                      <tr key={index} className={`border-t hover:bg-gray-50 ${expense.isDuplicate ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3">
                          {expense.isDuplicate ? (
                            <div className="flex flex-col space-y-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Duplicate
                              </span>
                              <AppSelect
                                value={expense.duplicateAction}
                                onChange={(e) => handleDuplicateActionChange(index, e.target.value)}
                                aria-label="Duplicate action"
                                sheetTitle="Duplicate action"
                                options={[
                                  { value: 'skip', label: 'Skip' },
                                  { value: 'update', label: 'Update' },
                                  { value: 'merge', label: 'Merge' },
                                ]}
                                triggerClassName="text-xs border border-gray-300 rounded px-1 py-0.5 min-h-[28px] flex items-center justify-between gap-1 touch-manipulation w-full"
                              />
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{expense.head}</td>
                        <td className="px-4 py-3 text-gray-600">₹{expense.amount}</td>
                        <td className="px-4 py-3 text-gray-600">{new Date(expense.date).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-gray-600">{expense.description}</td>
                        <td className="px-4 py-3 text-gray-600">{expense.paymentMode}</td>
                        <td className="px-4 py-3 text-gray-600">{expense.group}</td>
                        <td className="px-4 py-3 text-gray-600">{expense.employeeName}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEditExpense(index)}
                            className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
              >
                {isImporting ? 'Importing...' : `Import ${mappedData.length} Expenses`}
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            {/* Edit Modal */}
            {editingIndex !== null && (
              <EditExpenseModal
                expense={mappedData[editingIndex]}
                onSave={(updatedExpense) => handleSaveEdit(editingIndex, updatedExpense)}
                onCancel={handleCancelEdit}
              />
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl text-purple-500 mb-4">✅</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Completed Successfully!</h3>
            </div>

            {importResult && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">{importResult.success}</div>
                    <div className="text-gray-600">Expenses Imported</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{importResult.errors}</div>
                    <div className="text-gray-600">Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{importResult.total}</div>
                    <div className="text-gray-600">Total Processed</div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={resetImport}
                className="bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 font-medium"
              >
                Import More Expenses
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/import"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Import Selection
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? '✓' : step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`w-20 h-1 mx-2 ${
                        currentStep > step ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Upload File</span>
              <span>Map Fields</span>
              <span>Preview</span>
              <span>Complete</span>
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Expense Modal Component
const EditExpenseModal = ({ expense, onSave, onCancel }) => {
  const [editedExpense, setEditedExpense] = useState({ ...expense });

  const handleFieldChange = (field, value) => {
    setEditedExpense(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(editedExpense);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Edit Expense</h3>
            <button
              onClick={onCancel}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation" aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expense Head *</label>
              <input
                type="text"
                value={editedExpense.head}
                onChange={(e) => handleFieldChange('head', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                value={editedExpense.amount}
                onChange={(e) => handleFieldChange('amount', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={editedExpense.date ? new Date(editedExpense.date).toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange('date', new Date(e.target.value).toISOString())}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
              <AppSelect
                value={editedExpense.paymentMode}
                onChange={(e) => handleFieldChange('paymentMode', e.target.value)}
                aria-label="Payment mode"
                sheetTitle="Payment mode"
                options={[
                  { value: 'Cash', label: 'Cash' },
                  { value: 'Card', label: 'Card' },
                  { value: 'Bank Transfer', label: 'Bank Transfer' },
                  { value: 'UPI', label: 'UPI' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:ring-purple-500`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
              <AppSelect
                value={editedExpense.group}
                onChange={(e) => handleFieldChange('group', e.target.value)}
                aria-label="Group"
                sheetTitle="Group"
                options={[
                  { value: 'Fixed', label: 'Fixed' },
                  { value: 'Variable', label: 'Variable' },
                  { value: 'One-time', label: 'One-time' },
                  { value: 'Recurring', label: 'Recurring' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:ring-purple-500`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
              <input
                type="text"
                value={editedExpense.employeeName}
                onChange={(e) => handleFieldChange('employeeName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Number</label>
              <input
                type="text"
                value={editedExpense.receiptNumber}
                onChange={(e) => handleFieldChange('receiptNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={editedExpense.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={editedExpense.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesImport;
